import { ReactNode, useEffect, useState } from 'react';

import { UserFixedNotificationsFlow } from '../constants/UserFixedNotificationsFlow';
import { Notifications } from '../models/Notifications';
import { useUserFixedNotificationsStyles } from '../main/components/UserFixedNotifications/UserFixedNotifications.styles';
import { FlowPatternOption } from '../constants/FlowPatternsType';
import {
    getPaymentFailureNotificationTemplate,
    getPendingConfirmationNotificationTemplate,
} from '../main/components/UserFixedNotifications/paymentFixedNotificationsTemplates';
import { getEmailReminderNotificationTemplate } from '../main/components/UserFixedNotifications/verificationFixedNotificationsTemplates';
import {
    getAccountDisabledNotificationTemplate,
    getCancellationPendingNotificationTemplate,
    getDeveloperAccountNotificationTemplate,
    getDowngradeNotificationTemplate,
} from '../main/components/UserFixedNotifications/subscriptionFixedNotificationsTemplates';
import { User } from '../models/User';

export interface IUseUserFixedNotificationsFlowProps {
    user?: User;
    billingData: any;
    notifications: Notifications | null;
}

export interface IUseUserFixedNotificationsFlowExports {
    userFixedNotifications: FlowPatternOption<UserFixedNotificationsFlow, ReactNode>[];
}

export const useUserFixedNotificationsFlow = ({
    user,
    billingData,
    notifications,
}: IUseUserFixedNotificationsFlowProps): IUseUserFixedNotificationsFlowExports => {
    const [userFixedNotifications, setUserFixedNotifications] = useState<
        FlowPatternOption<UserFixedNotificationsFlow, ReactNode>[]
    >([]);

    const classes = useUserFixedNotificationsStyles();

    const reminderEmail = notifications?.updateEmail;

    const findAllValidUserFixedNotificationsOption = (
        userFixedNotificationsOptions: FlowPatternOption<UserFixedNotificationsFlow, ReactNode>[]
    ): FlowPatternOption<UserFixedNotificationsFlow, ReactNode>[] =>
        userFixedNotificationsOptions.filter((option: FlowPatternOption<UserFixedNotificationsFlow, ReactNode>) =>
            option.isValid()
        );

    const findUserFixedNotifications = (): FlowPatternOption<UserFixedNotificationsFlow, ReactNode>[] => {
        const userFixedNotificationsFlows: FlowPatternOption<UserFixedNotificationsFlow, ReactNode>[] = [
            {
                flow: UserFixedNotificationsFlow.developerAccount,
                isValid: () => notifications?.isDeveloper,
                action: () => getDeveloperAccountNotificationTemplate(classes.embeddedNotificationWrapper),
            },
            {
                flow: UserFixedNotificationsFlow.emailReminder,
                isValid: () => !!notifications?.updateEmail,
                action: () => getEmailReminderNotificationTemplate(classes.embeddedNotificationWrapper, reminderEmail),
            },
            {
                flow: UserFixedNotificationsFlow.downgraded,
                isValid: () => !!notifications?.pendingDowngradeDetails,
                action: () =>
                    notifications?.pendingDowngradeDetails &&
                    getDowngradeNotificationTemplate(classes, notifications?.pendingDowngradeDetails),
            },
            {
                flow: UserFixedNotificationsFlow.cancellationPending,
                isValid: () => !!notifications?.pendingCancellationDetails,
                action: () =>
                    notifications?.pendingCancellationDetails &&
                    getCancellationPendingNotificationTemplate(classes, notifications?.pendingCancellationDetails),
            },
            {
                flow: UserFixedNotificationsFlow.accountDisabled,
                isValid: () => notifications?.accountDisabled && billingData,
                action: () => getAccountDisabledNotificationTemplate(classes.embeddedNotificationWrapper, billingData),
            },
            {
                flow: UserFixedNotificationsFlow.paymentFailure,
                isValid: () => notifications?.lastPaymentFailed,
                action: () => getPaymentFailureNotificationTemplate(classes, notifications?.remainingChargeAttempts),
            },
            {
                flow: UserFixedNotificationsFlow.pendingConfirmation,
                isValid: () => notifications?.hasPendingMethodChange,
                action: () => getPendingConfirmationNotificationTemplate(classes),
            },
        ];

        return findAllValidUserFixedNotificationsOption(userFixedNotificationsFlows);
    };

    useEffect(() => {
        if (user?.accountId) {
            setUserFixedNotifications(findUserFixedNotifications());
        }
    }, [user]);

    return {
        userFixedNotifications,
    };
};
